import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import {
  Card,
  Input,
  Button,
  Tag,
  Space,
  Typography,
  Empty,
  Spin,
  Row,
  Col,
} from 'antd';
import { PlusOutlined, CloseOutlined, SearchOutlined } from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import { SortableContext, useSortable, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  getAdminSearchSuggestions,
  searchTools as searchToolsQuery,
} from '../../graphql/queries';
import {
  createSearchSuggestion,
  deleteSearchSuggestion,
  reorderSearchSuggestions,
} from '../../graphql/mutation';
import { showSnackBar } from '../../components/SnackBar/SnackBar';

function SortableWordTag({ id, term, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    cursor: 'grab',
  };

  return (
    <Tag
      ref={setNodeRef}
      style={{ ...style, fontSize: 13, padding: '4px 8px', marginBottom: 8 }}
      {...attributes}
      {...listeners}
    >
      {term}
      <CloseOutlined
        style={{ marginLeft: 6, fontSize: 10 }}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => onDelete(id)}
      />
    </Tag>
  );
}

const { Title, Text } = Typography;

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

export default function Search() {
  const [newTerm, setNewTerm] = useState('');
  const [demoQuery, setDemoQuery] = useState('');
  const debounceRef = useRef(null);

  const { data, loading, refetch } = useQuery(getAdminSearchSuggestions, {
    fetchPolicy: 'network-only',
  });
  const suggestions = data?.getAdminSearchSuggestions ?? [];

  const [createSuggestion] = useMutation(createSearchSuggestion);
  const [deleteSuggestion] = useMutation(deleteSearchSuggestion);
  const [reorderSuggestions] = useMutation(reorderSearchSuggestions);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const [runSearch, { data: searchData, loading: searchLoading }] = useLazyQuery(
    searchToolsQuery,
    { fetchPolicy: 'network-only' },
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = demoQuery.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) return;
    debounceRef.current = setTimeout(() => {
      runSearch({ variables: { query: trimmed, limit: 10, simulateReviewMode: true } });
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [demoQuery, runSearch]);

  const handleAdd = async () => {
    const term = newTerm.trim();
    if (!term) return;
    try {
      await createSuggestion({ variables: { input: { term } } });
      setNewTerm('');
      refetch();
    } catch (err) {
      showSnackBar('error', 'Error', err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSuggestion({ variables: { id } });
      refetch();
    } catch (err) {
      showSnackBar('error', 'Error', err.message);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = suggestions.findIndex((s) => s.id === active.id);
    const newIndex = suggestions.findIndex((s) => s.id === over.id);
    const reordered = arrayMove(suggestions, oldIndex, newIndex);

    try {
      await reorderSuggestions({ variables: { orderedIds: reordered.map((s) => s.id) } });
      refetch();
    } catch (err) {
      showSnackBar('error', 'Error', err.message);
    }
  };

  const results = searchData?.searchTools ?? [];

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <Title level={3}>Search</Title>
      <Row gutter={24} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Search Demo">
          
            <div style={styles.phoneWrap}>
              <div style={styles.phone}>
                <div style={styles.notch} />
                <div style={styles.screen}>
                  <div style={styles.searchBar}>
                    <SearchOutlined style={{ color: '#8e8e93', fontSize: 16 }} />
                    <input
                      value={demoQuery}
                      onChange={(e) => setDemoQuery(e.target.value)}
                      placeholder="Search tools..."
                      style={styles.input}
                    />
                  </div>

                  {suggestions.length > 0 && (
                    <div style={styles.suggestionsRow}>
                      {suggestions.map((s) => (
                        <Tag
                          key={s.id}
                          color={s.term === demoQuery ? 'blue' : 'default'}
                          style={{ cursor: 'pointer', marginBottom: 6 }}
                          onClick={() => setDemoQuery(s.term)}
                        >
                          {s.term}
                        </Tag>
                      ))}
                    </div>
                  )}

                  <div style={styles.resultsArea}>
                    {demoQuery.trim().length < MIN_QUERY_LENGTH ? (
                      <div style={styles.hint}>
                        <Text type="secondary">Type at least {MIN_QUERY_LENGTH} characters</Text>
                      </div>
                    ) : searchLoading ? (
                      <div style={styles.hint}>
                        <Spin size="small" />
                      </div>
                    ) : results.length === 0 ? (
                      <div style={styles.hint}>
                        <Empty description="No tools found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                      </div>
                    ) : (
                      results.map((tool) => {
                        const q = demoQuery.trim().toLowerCase();
                        const nameMatches = tool.name.toLowerCase().includes(q);
                        return (
                          <div key={tool.id} style={styles.resultRow}>
                            <div
                              style={{
                                ...styles.thumb,
                                backgroundImage: tool.bannerUrl
                                  ? `url(${tool.bannerUrl})`
                                  : undefined,
                              }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Text strong style={{ fontSize: 14 }} ellipsis>
                                  {tool.name}
                                </Text>
                                {nameMatches && (
                                  <Tag color="green" style={{ fontSize: 10, padding: '0 4px' }}>
                                    name
                                  </Tag>
                                )}
                                {tool.isFeatured && (
                                  <Tag color="gold" style={{ fontSize: 10, padding: '0 4px' }}>
                                    featured
                                  </Tag>
                                )}
                              </div>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {tool.category?.name ?? 'Uncategorized'}
                              </Text>
                              <div
                                style={{ marginTop: 4, overflow: 'hidden', whiteSpace: 'nowrap' }}
                              >
                                {(tool.keywords || []).slice(0, 4).map((k) => (
                                  <Tag
                                    key={k}
                                    color={k.toLowerCase().includes(q) ? 'blue' : 'default'}
                                    style={{ fontSize: 10, padding: '0 4px', marginRight: 4 }}
                                  >
                                    {k}
                                  </Tag>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Search Words">

            <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
              <Input
                placeholder="e.g. tiktok dance"
                value={newTerm}
                onChange={(e) => setNewTerm(e.target.value)}
                onPressEnter={handleAdd}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                Add
              </Button>
            </Space.Compact>

            {loading ? (
              <Spin />
            ) : suggestions.length === 0 ? (
              <Empty description="No search words yet" />
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={suggestions.map((s) => s.id)}
                  strategy={rectSortingStrategy}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {suggestions.map((s) => (
                      <SortableWordTag
                        key={s.id}
                        id={s.id}
                        term={s.term}
                        onDelete={handleDelete}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}

const styles = {
  phoneWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: '16px 0',
  },
  phone: {
    width: 340,
    height: 680,
    borderRadius: 40,
    background: '#111',
    padding: 12,
    boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
    position: 'relative',
  },
  notch: {
    position: 'absolute',
    top: 12,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 120,
    height: 24,
    background: '#111',
    borderRadius: 14,
    zIndex: 2,
  },
  screen: {
    width: '100%',
    height: '100%',
    background: '#f5f5f7',
    borderRadius: 28,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 40,
  },
  searchBar: {
    margin: '0 16px 10px',
    background: '#e9e9eb',
    borderRadius: 10,
    padding: '8px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  input: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    flex: 1,
    fontSize: 15,
  },
  suggestionsRow: {
    margin: '0 16px 10px',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
  },
  resultsArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 12px 16px',
  },
  hint: {
    display: 'flex',
    justifyContent: 'center',
    padding: '24px 0',
  },
  resultRow: {
    display: 'flex',
    gap: 10,
    padding: '8px 4px',
    borderBottom: '1px solid #e5e5ea',
  },
  thumb: {
    width: 48,
    height: 48,
    borderRadius: 8,
    background: '#d9d9d9',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    flexShrink: 0,
  },
};
